import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, HeartPulse, Phone, Mail, HeartHandshake } from "lucide-react";
import { NAV_LINKS, ORG } from "../data/content";

const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-hecso-green text-white">
      <HeartPulse className="w-6 h-6" />
    </span>
    <span className="leading-tight">
      <span className="block font-heading text-base sm:text-lg font-semibold text-hecso-greendark whitespace-nowrap">
        {ORG.name}
      </span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50" data-testid="main-header">
      <div className="hidden md:block bg-hecso-greendark text-white/85">
        <div className="container-hecso flex items-center justify-center py-1.5 text-xs">
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> {ORG.phone}</span>
            <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> {ORG.email}</span>
          </div>
        </div>
      </div>

      <div className="bg-white/95 backdrop-blur-md border-b border-emerald-100 shadow-sm">
        <div className="container-hecso flex items-center justify-between py-3">
          <Logo />
          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "text-hecso-green bg-hecso-greensubtle" : "text-hecso-ink hover:text-hecso-green hover:bg-hecso-greensubtle"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden xl:flex items-center gap-3">
            <Link
              to="/get-involved"
              data-testid="nav-volunteer-btn"
              className="rounded-lg border-2 border-hecso-green px-4 py-2 text-sm font-semibold text-hecso-green transition-colors hover:bg-hecso-green hover:text-white"
            >
              Volunteer
            </Link>
            <Link
              to="/donate"
              data-testid="nav-donate-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-hecso-orange px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-hecso-orangehover hover:-translate-y-0.5 active:scale-95"
            >
              <HeartHandshake className="w-4 h-4" /> Donate Now
            </Link>
          </div>
          <button
            className="xl:hidden p-2 rounded-md text-hecso-ink hover:bg-hecso-greensubtle"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden border-t border-emerald-100 bg-white" data-testid="mobile-menu">
            <nav className="container-hecso py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={() =>
                    `px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      location.pathname === l.path
                        ? "text-hecso-green bg-hecso-greensubtle"
                        : "text-hecso-ink hover:bg-hecso-greensubtle"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex gap-3 pt-3">
                <Link
                  to="/get-involved"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-volunteer-btn"
                  className="flex-1 text-center rounded-lg border-2 border-hecso-green px-4 py-2.5 text-sm font-semibold text-hecso-green"
                >
                  Volunteer
                </Link>
                <Link
                  to="/donate"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-donate-btn"
                  className="flex-1 text-center rounded-lg bg-hecso-orange px-4 py-2.5 text-sm font-semibold text-white"
                >
                  Donate Now
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
